import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { resolveScope, type AccessScope, type Permission } from '@hrms/shared';
import { clearAccessToken, setAccessToken } from '@/lib/access-token';
import { onSessionExpired } from '@/lib/api-client';
import { forgetThisDevice } from '@/features/chat/use-push-registration';
import { authApi, type AuthSession, type LoginPayload, type SessionUser } from './auth-api';

interface AuthContextValue {
  user: SessionUser | null;
  /** True until the initial silent refresh settles — render nothing decisive. */
  isBootstrapping: boolean;
  isAuthenticated: boolean;
  login: (payload: LoginPayload) => Promise<SessionUser>;
  logout: () => Promise<void>;
  /** Applies a session returned by an endpoint that re-issues one. */
  applySession: (session: AuthSession) => void;
  can: (permission: Permission) => boolean;
  canAny: (...permissions: Permission[]) => boolean;
  scopeFor: (resource: string, action: string) => AccessScope | null;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const queryClient = useQueryClient();

  // The axios interceptor calls this from outside React when a refresh fails.
  // `setUser` is stable and `queryClient` is a singleton, so the handler needs
  // no ref indirection to stay current.
  useEffect(() => {
    onSessionExpired(() => {
      setUser(null);
      clearAccessToken();
      queryClient.clear();
    });
  }, [queryClient]);

  /**
   * Re-establishes the session on a cold load.
   *
   * The access token lives in memory, so a reload always starts signed out as
   * far as the app knows. The httpOnly refresh cookie is the only thing that
   * survives, and this exchanges it for a fresh pair. A 401 here is the normal
   * case for a genuinely signed-out visitor, not an error worth surfacing.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const session = await authApi.refresh();
        if (cancelled) return;
        setAccessToken(session.accessToken);
        setUser(session.user);
      } catch {
        if (!cancelled) {
          clearAccessToken();
          setUser(null);
        }
      } finally {
        if (!cancelled) setIsBootstrapping(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const applySession = useCallback((session: AuthSession) => {
    setAccessToken(session.accessToken);
    setUser(session.user);
  }, []);

  const login = useCallback(
    async (payload: LoginPayload) => {
      const session = await authApi.login(payload);
      applySession(session);
      return session.user;
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    // Before the session ends, while the request still carries it: tell the
    // server to stop pushing to this handset. A phone handed to a colleague
    // must not keep buzzing with the last person's messages, and the token
    // outlives the session — Google would happily deliver to it for months.
    await forgetThisDevice();
    try {
      await authApi.logout();
    } catch {
      // A failed call still ends the session locally — leaving the user
      // apparently signed in because the network blipped is worse.
    } finally {
      clearAccessToken();
      setUser(null);
      // Drops every cached query. Without this, the next person to sign in on
      // this browser briefly sees the previous user's data.
      queryClient.clear();
    }
  }, [queryClient]);

  const permissionSet = useMemo(
    () => new Set<Permission>(user?.permissions ?? []),
    [user?.permissions],
  );

  const can = useCallback(
    (permission: Permission) => permissionSet.has(permission),
    [permissionSet],
  );

  const canAny = useCallback(
    (...permissions: Permission[]) => permissions.some((p) => permissionSet.has(p)),
    [permissionSet],
  );

  const scopeFor = useCallback(
    (resource: string, action: string) =>
      resolveScope(permissionSet as ReadonlySet<string>, resource, action),
    [permissionSet],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isBootstrapping,
      isAuthenticated: user !== null,
      login,
      logout,
      applySession,
      can,
      canAny,
      scopeFor,
    }),
    [user, isBootstrapping, login, logout, applySession, can, canAny, scopeFor],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
